// The agent inspector's geometry and state (shell-ia §12.1; DESIGN-SYSTEM §00.9 over §2, §10.15; U-15): open by default from 1280 px
// (360, resizable 360–560), the 52 px rail at 1024–1279 (opening floats it over the content, Esc returns it), a sheet below 1024.
// The stored choice is `pref:econic.chat` {state, width}; a breakpoint chooses the *effective* state and never rewrites the choice. If the
// content would be narrower than 760 px, the column shows as its rail. A screen whose own content is the chat (Today's Simple view, where the
// big box is the agent) asks for the rail with `useRailHere()`: the column starts folded to its rail there, opening it floats the overlay, and
// the person's stored choice is untouched, so every other screen shows the column as they left it. WP-SHELL owns this container; the
// sidebar's content is WP-SIDEBAR's (econic/sidebar, which reads `useSidebarState()` here).
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router";
import { useMediaQuery } from "../../lib/media";
import { routeFor } from "../../app/routes";
import { useDockAvailable } from "../../shell/dock";
import { usePref } from "../prefs";

export type SidebarChoice = "open" | "rail" | "hidden";
export type SidebarEffective = "open" | "rail" | "overlay" | "hidden" | "sheet";

/** ⌘J under ECONIC: the agent column starts a fresh follow-me conversation (ChatSidebar listens; the shell dispatches it). */
export const AGENT_NEW_EVENT = "econic:agent-new";

export const SIDEBAR_MIN = 360;
export const SIDEBAR_MAX = 560;
export const SIDEBAR_RAIL = 52;
const CONTENT_MIN = 760;

interface ChatPref { state?: SidebarChoice; width?: number; pinnedThread?: string }

export interface SidebarState {
  /** What the person chose (persisted). */
  choice: SidebarChoice;
  /** What the column shows now. */
  effective: SidebarEffective;
  /** Width of the open column (px). */
  width: number;
  /** Width the content gives up to the column (0 | 52 | width). */
  reserved: number;
  setChoice: (s: SidebarChoice) => void;
  setWidth: (w: number, persist?: boolean) => void;
  /** Opens the column (or its overlay / sheet) wherever the person is. */
  open: () => void;
  /** ⌘. — open ↔ rail (wide), overlay on/off (rail widths), sheet on/off (narrow). */
  toggle: () => void;
  /** Esc on an overlay or sheet; the rail button "collapse". */
  close: () => void;
  sheetOpen: boolean;
  setSheetOpen: (o: boolean) => void;
  /** The screen (its pathname) that asked for the rail with `useRailHere()`, or null. */
  setRailAt: (pathname: string | null) => void;
}

const SidebarContext = createContext<SidebarState | null>(null);

const isChat = (v: unknown): v is ChatPref => !!v && typeof v === "object";

export function SidebarStateProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const available = useDockAvailable();
  const def = routeFor(pathname);
  const wide = useMediaQuery("(min-width: 1280px)");
  const mid = useMediaQuery("(min-width: 1024px)");
  const [pref, setPref] = usePref<ChatPref>("econic.chat", {}, isChat);
  const [overlay, setOverlay] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const [railAt, setRailAt] = useState<string | null>(null);
  const [viewport, setViewport] = useState(() => (typeof window === "undefined" ? 1440 : window.innerWidth));
  useEffect(() => {
    const on = () => setViewport(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);

  const choice: SidebarChoice = pref.state === "rail" || pref.state === "hidden" ? pref.state : "open";
  const storedWidth = typeof pref.width === "number" ? Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, pref.width)) : null;
  const width = dragWidth ?? storedWidth ?? SIDEBAR_MIN;
  // The route's own rail, or the screen's (useRailHere) unless the person hid the column altogether.
  const routeRail = def?.sidebar === "rail" || (railAt === pathname && choice !== "hidden");
  const none = !available || def?.sidebar === "none";

  let effective: SidebarEffective;
  if (none) effective = "hidden";
  else if (!mid) effective = sheetOpen ? "sheet" : "hidden";
  else if (!wide || routeRail || viewport - width < CONTENT_MIN) effective = overlay ? "overlay" : "rail";
  else effective = choice === "open" ? "open" : choice === "rail" ? (overlay ? "overlay" : "rail") : "hidden";
  const reserved = effective === "open" ? width : effective === "rail" || effective === "overlay" ? SIDEBAR_RAIL : 0;

  // Leaving a screen closes a floating overlay or sheet; the docked column stays as it was (it follows the person).
  useEffect(() => {
    setOverlay(false);
    setSheetOpen(false);
  }, [pathname]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--econic-sidebar-w", `${reserved}px`);
    root.dataset.sidebar = effective;
    return () => {
      root.style.removeProperty("--econic-sidebar-w");
      delete root.dataset.sidebar;
    };
  }, [reserved, effective]);

  const setChoice = useCallback((s: SidebarChoice) => setPref({ ...pref, state: s }), [pref, setPref]);
  const setWidth = useCallback((w: number, persist = false) => {
    const next = Math.round(Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, w)));
    if (persist) {
      setDragWidth(null);
      setPref({ ...pref, state: "open", width: next });
    } else setDragWidth(next);
  }, [pref, setPref]);

  const open = useCallback(() => {
    if (none) return;
    if (!mid) setSheetOpen(true);
    else if (!wide || routeRail || viewport - width < CONTENT_MIN) setOverlay(true);
    else if (choice !== "open") setChoice("open");
  }, [none, mid, wide, routeRail, viewport, width, choice, setChoice]);
  const close = useCallback(() => {
    if (effective === "sheet") setSheetOpen(false);
    else if (effective === "overlay") setOverlay(false);
    else if (effective === "open") setChoice("rail");
  }, [effective, setChoice]);
  const toggle = useCallback(() => {
    if (effective === "open" || effective === "overlay" || effective === "sheet") close();
    else open();
  }, [effective, open, close]);

  const value = useMemo<SidebarState>(() => ({ choice, effective, width, reserved, setChoice, setWidth, open, toggle, close, sheetOpen, setSheetOpen, setRailAt }),
    [choice, effective, width, reserved, setChoice, setWidth, open, toggle, close, sheetOpen]);
  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebarState(): SidebarState {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error("useSidebarState must be used inside the ECONIC shell.");
  return ctx;
}

/**
 * The screen's content is the chat (Today's Simple view): while it is mounted, the agent column starts as its rail on this pathname and
 * opens as the overlay, one click away. `active` false (another view of the same screen) leaves the column as the person chose.
 */
export function useRailHere(active = true): void {
  const ctx = useContext(SidebarContext);
  const set = ctx?.setRailAt;
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    if (!set || !active) return;
    set(pathname);
    return () => set(null);
  }, [set, active, pathname]);
}

/** Outside the ECONIC shell (os, legacy) there is no column. */
export function useSidebarStateOptional(): SidebarState | null {
  return useContext(SidebarContext);
}
